import React, { useCallback, useEffect, useRef, useState } from "react";
import { animate, motion, AnimatePresence, useMotionValue } from "framer-motion";
import { EASE, fadeUpNoBlur } from "../../lib/motion";
import { resolveImg } from "../../lib/image";

const VIEWPORT_H = 700;
const CARD_H = 460;
const GAP = 18;
const STEP = CARD_H + GAP;
const PAD = (VIEWPORT_H - CARD_H) / 2;

const highlight = (quote, h) => {
    if (!h) return quote;
    const i = quote.toLowerCase().indexOf(h.toLowerCase());
    if (i < 0) return quote;
    return (
        <>
            {quote.slice(0, i)}
            <span style={{ color: "#7FB3E0" }}>{quote.slice(i, i + h.length)}</span>
            {quote.slice(i + h.length)}
        </>
    );
};

export const Testimonials = ({ content }) => {
    const t = content?.testimonials || {};
    const items = (t.items || []).filter(Boolean);
    const rotationSeconds = Math.max(2, Number(t.rotation_seconds) || 6);

    // visualStep can range 0..items.length (items.length is the duplicate slot at the bottom).
    const [visualStep, setVisualStep] = useState(0);
    const yMotion = useMotionValue(PAD);
    const animatingRef = useRef(false);

    const stack = items.length > 0 ? items.concat([items[0]]) : [];
    const realIdx = visualStep % items.length || 0;

    const goNext = useCallback(async () => {
        if (items.length <= 1 || animatingRef.current) return;
        animatingRef.current = true;
        const next = visualStep + 1;
        setVisualStep(next);
        await animate(yMotion, PAD - next * STEP, { duration: 1.15, ease: EASE });
        if (next >= items.length) {
            // Snap back to top without animation. Visually identical content
            // (both slot items.length and slot 0 render items[0]), so it reads
            // as a continuous upward scroll.
            yMotion.set(PAD);
            setVisualStep(0);
        }
        animatingRef.current = false;
    }, [items.length, yMotion, visualStep]);

    const goTo = useCallback(async (targetIdx) => {
        if (items.length <= 1) return;
        animatingRef.current = true;
        setVisualStep(targetIdx);
        await animate(yMotion, PAD - targetIdx * STEP, { duration: 0.9, ease: EASE });
        animatingRef.current = false;
    }, [items.length, yMotion]);

    useEffect(() => {
        if (items.length <= 1) return;
        const interval = setInterval(goNext, rotationSeconds * 1000);
        return () => clearInterval(interval);
    }, [items.length, rotationSeconds, goNext]);

    const current = items[realIdx];
    if (!current) return null;

    return (
        <section
            className="relative py-6 md:py-20 bg-[#0A0A0A] text-white overflow-hidden"
            data-testid="testimonials-section"
        >
            <div
                aria-hidden
                className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(11,59,96,0.22) 0%, rgba(11,59,96,0) 70%)" }}
            />

            <div className="relative mx-auto max-w-[1240px] px-6 md:px-10">
                <motion.div {...fadeUpNoBlur(0)} className="inline-flex items-center gap-2.5">
                    <span className="text-xs uppercase tracking-[0.24em] text-white/55 font-body">
                        {t.eyebrow}
                    </span>
                    <span className="rounded-full border border-white/20 px-3.5 py-1 text-xs uppercase tracking-[0.22em] text-white/85 font-body">
                        Say
                    </span>
                </motion.div>

                {/* ─── MOBILE: cinematic single-card stack ───────────────────────── */}
                <div className="mt-8 md:hidden" data-testid="testimonials-mobile">
                    <div className="relative rounded-[28px] overflow-hidden bg-[#111]" style={{ aspectRatio: "4 / 5" }}>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={`mc-${realIdx}`}
                                initial={{ opacity: 0, scale: 1.02 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.99 }}
                                transition={{ duration: 0.9, ease: EASE }}
                                className="absolute inset-0"
                                onClick={() => goTo((realIdx + 1) % items.length)}
                                data-testid={`testimonial-mobile-card-${realIdx}`}
                            >
                                <img
                                    src={resolveImg(current.portrait)}
                                    alt={current.author}
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover"
                                />
                                {/* Dark gradient — keeps photo readable in upper half, ink-dense in lower half */}
                                <div
                                    aria-hidden
                                    className="absolute inset-0"
                                    style={{
                                        background:
                                            "linear-gradient(to bottom, rgba(10,10,10,0) 0%, rgba(10,10,10,0) 40%, rgba(10,10,10,0.55) 65%, rgba(10,10,10,0.92) 100%)",
                                    }}
                                />
                                {/* Quote + author overlay */}
                                <div className="absolute inset-x-0 bottom-0 p-6 pb-7">
                                    <blockquote
                                        className="font-display font-semibold text-[22px] leading-[1.12] tracking-[-0.02em] text-white"
                                        data-testid="testimonial-mobile-quote"
                                    >
                                        {highlight(current.quote, current.highlight)}
                                    </blockquote>
                                    <div className="mt-5 font-body text-[13px] text-white/65" data-testid="testimonial-mobile-author">
                                        <div className="text-white/95 font-medium">{current.author}</div>
                                        {current.role && <div className="mt-0.5">{current.role}</div>}
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="mt-5 flex gap-2 justify-center">
                        {items.map((it, i) => (
                            <button
                                key={`dot-m-${it.author || i}`}
                                onClick={() => goTo(i)}
                                aria-label={`Show testimonial ${i + 1}`}
                                className={`h-1 rounded-full transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                                    i === realIdx ? "w-10 bg-white" : "w-5 bg-white/25 hover:bg-white/50"
                                }`}
                                data-testid={`testimonial-dot-mobile-${i}`}
                            />
                        ))}
                    </div>
                </div>

                {/* ─── DESKTOP: original filmstrip layout ─────────────────────────── */}
                <div className="hidden md:grid mt-10 grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <div className="lg:col-span-7 min-h-[460px] flex flex-col justify-center" data-testid="testimonials-quote-col">
                        <AnimatePresence mode="wait">
                            <motion.blockquote
                                key={`q-${realIdx}`}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -14 }}
                                transition={{ duration: 0.9, ease: EASE }}
                                className="font-display font-semibold text-[28px] sm:text-[40px] md:text-[48px] lg:text-[56px] leading-[1.08] tracking-[-0.025em] max-w-[680px]"
                                data-testid="testimonial-quote"
                            >
                                {highlight(current.quote, current.highlight)}
                            </motion.blockquote>
                        </AnimatePresence>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={`a-${realIdx}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
                                className="mt-10 font-body text-sm text-white/55"
                                data-testid="testimonial-author"
                            >
                                <div className="text-white/85 font-medium">{current.author}</div>
                                {current.role && <div className="mt-1">{current.role}</div>}
                            </motion.div>
                        </AnimatePresence>

                        <div className="mt-8 flex gap-2">
                            {items.map((it, i) => (
                                <button
                                    key={`dot-d-${it.author || i}`}
                                    onClick={() => goTo(i)}
                                    aria-label={`Show testimonial ${i + 1}`}
                                    className={`h-1 rounded-full transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                                        i === realIdx ? "w-12 bg-white" : "w-6 bg-white/25 hover:bg-white/50"
                                    }`}
                                    data-testid={`testimonial-dot-${i}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div
                            className="relative mx-auto w-full max-w-[380px]"
                            style={{
                                height: VIEWPORT_H,
                                WebkitMaskImage:
                                    "linear-gradient(to bottom, transparent 0%, black 14%, black 86%, transparent 100%)",
                                maskImage:
                                    "linear-gradient(to bottom, transparent 0%, black 14%, black 86%, transparent 100%)",
                            }}
                            data-testid="testimonial-filmstrip"
                        >
                            <motion.div
                                style={{ y: yMotion, willChange: "transform" }}
                                className="flex flex-col"
                            >
                                {stack.map((it, i) => {
                                    const realIndex = i % items.length;
                                    const isActive = i === visualStep;
                                    return (
                                        <motion.div
                                            key={`${realIndex}-${i}`}
                                            animate={{
                                                scale: isActive ? 1 : 0.94,
                                                opacity: isActive ? 1 : 0.45,
                                                filter: isActive ? "saturate(1) brightness(1)" : "saturate(0.7) brightness(0.7)",
                                            }}
                                            transition={{ duration: 0.9, ease: EASE }}
                                            className="rounded-[28px] overflow-hidden shrink-0 cursor-pointer"
                                            style={{ height: CARD_H, marginBottom: GAP, transform: "translateZ(0)" }}
                                            data-testid={`testimonial-portrait-${realIndex}-${i}`}
                                            onClick={() => goTo(realIndex)}
                                        >
                                            <img
                                                src={resolveImg(it.portrait)}
                                                alt={it.author}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-full h-full object-cover"
                                            />
                                        </motion.div>
                                    );
                                })}
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
